import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FieldWrapper } from '../../Form/FieldWrapper';
import { twMerge } from 'tailwind-merge';

const sizes = {
  xs: 'w-32',
  sm: 'w-40',
  md: 'w-60',
  lg: 'w-80',
  xl: 'w-96',
  full: 'w-full',
};

export const Input = React.forwardRef(
  (
    {
      type = 'text',
      label,
      className,
      wrapperClassName,
      registration,
      error,
      placeholder = 'Placeholder',
      defaultValue,
      name,
      size = 'md',
      onChange = () => {},
      value,
      disabled = false,
      icon,
      onKeyDown,
      iconClassName,
      labelClassName,
      button,
      labelTextClassName,
      clearIcon,
      clearIconClassName,
      clearIconClick,
      multiline = false,
      textAreaRows,
      setTextAreaRows,
      onNewDeepDiveClick,
      setOnNewDeepDiveClick,
      ...remainingProps
    },
    ref
  ) => {
    const inputRef = useRef();

    const handleTextAreaChange = (event) => {
      const textareaLineHeight = 20;
      const previousRows = event.target.rows;
      event.target.rows = 1;

      const currentRows = ~~(event.target.scrollHeight / textareaLineHeight);

      if (currentRows === previousRows) {
        event.target.rows = currentRows;
      }

      if (currentRows >= 5) {
        event.target.rows = 5;
        event.target.style.overflowY = 'scroll';
      } else {
        event.target.rows = currentRows;
        event.target.style.overflowY = 'hidden';
      }

      setTextAreaRows(currentRows < 5 ? currentRows : 5);
      onChange(event); // assuming you have a prop `onChange`
    };

    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(inputRef.current);
        } else {
          ref.current = inputRef.current;
        }
      }
    }, [ref]);

    useEffect(() => {
      if (onNewDeepDiveClick) {
        inputRef.current.focus();
        setOnNewDeepDiveClick(false);
      }
    }, [onNewDeepDiveClick]);

    const inputElement = multiline ? (
      <textarea
        ref={inputRef}
        onChange={handleTextAreaChange}
        onKeyDown={onKeyDown}
        disabled={disabled}
        name={name}
        className={twMerge(
          'resize-none appearance-none w-full block px-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-brand-primary-500 sm:text-sm',
          className
        )}
        {...registration}
        defaultValue={defaultValue}
        value={value}
        placeholder={placeholder}
        rows={textAreaRows}
        {...remainingProps}
      />
    ) : (
      <input
        ref={inputRef}
        onChange={(event) => onChange(event)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        name={name}
        type={type}
        className={twMerge(
          'relative appearance-none w-full h-9 block px-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-brand-primary-500 sm:text-sm',
          className
        )}
        {...registration}
        defaultValue={defaultValue}
        value={value}
        placeholder={placeholder}
        {...remainingProps}
      />
    );

    return (
      <FieldWrapper
        label={label}
        error={error}
        wrapperClassName={wrapperClassName}
        className={labelClassName}
        labelTextClassName={labelTextClassName}
      >
        <div
          className={`w-full ${twMerge(icon && 'block relative', sizes[size])}`}
        >
          {inputElement}
          {icon && (
            <span
              className={`absolute inset-y-0 ${iconClassName} flex items-center pr-3`}
            >
              {icon}
            </span>
          )}
          {clearIcon && (
            <span
              className={`absolute inset-y-0 ${clearIconClassName} flex items-center pr-2 cursor-pointer`}
              onClick={clearIconClick}
            >
              {value && clearIcon}
            </span>
          )}
        </div>
      </FieldWrapper>
    );
  }
);

Input.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'full']),
  name: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.element,
  onChange: PropTypes.func,
  multiline: PropTypes.bool,
  multiLineRows: PropTypes.number,
};
